import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cl from 'classnames';
import { useAppDispatch } from 'src/store';
import { usePage, useLanguage } from 'src/hooks';
import { useAccount, logoutAction } from 'src/services/auth';
import styles from './authStyle.module.scss';

export const Auth = () => {
  const [expanded, setExpanded] = useState(false);
  const dispatch = useAppDispatch();
  const account = useAccount();
  const navigate = useNavigate();
  const { homePage } = usePage();
  const { t } = useLanguage();

  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocumentClick = (event: Event) => {
      if (
        event.target instanceof Node &&
        listRef.current &&
        !listRef.current.contains(event.target)
      ) {
        setExpanded(false);
      }
    };

    if (expanded) {
      document.addEventListener('click', onDocumentClick);
    }

    return () => {
      if (expanded) {
        document.removeEventListener('click', onDocumentClick);
      }
    };
  }, [expanded]);

  return (
    <div
      onClick={(event) => {
        event.stopPropagation();
        setExpanded((prevState) => !prevState);
      }}
      className={styles.button}
    >
      <div className={styles.userIcon}>
        <svg
          width="22"
          height="26"
          viewBox="0 0 22 26"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="22" height="26" rx="11" fill="#1F93FF" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M20.8351 24.3863H2V22.3791C2.00538 21.672 2.19798 20.9801 2.5568 20.3785C2.91562 19.7769 3.42691 19.2888 4.03503 18.9673L7.55815 17.0548C7.66629 16.9971 7.75709 16.9096 7.82035 16.802C7.88362 16.6944 7.91692 16.5708 7.91676 16.4448V15.2602C7.31175 14.4294 6.85623 13.4926 6.57204 12.4945C6.37631 12.3191 6.21912 12.1023 6.11089 11.8587C6.00266 11.6152 5.94593 11.3505 5.94455 11.0824V9.51252C5.94734 9.09265 6.08621 8.68592 6.33895 8.3576V6.40797C6.30238 5.90104 6.36452 5.39167 6.52159 4.91005C6.67866 4.42843 6.92749 3.98438 7.25338 3.60423C8.43941 2.63049 9.90851 2.10059 11.422 2.10059C12.9356 2.10059 14.4047 2.63049 15.5907 3.60423C15.916 3.98443 16.1638 4.42872 16.3194 4.91048C16.475 5.39225 16.5352 5.90158 16.4962 6.40797V8.3576C16.7486 8.68539 16.8874 9.09142 16.8906 9.51066V11.0815C16.8879 11.4255 16.7947 11.7622 16.6209 12.0554C16.4471 12.3485 16.1995 12.587 15.9045 12.7452C15.6218 13.5404 15.2325 14.2905 14.748 14.9733C14.6673 15.081 14.5956 15.1794 14.5238 15.2686V16.4755C14.5254 16.6062 14.5624 16.734 14.6305 16.8441C14.6987 16.9542 14.7953 17.0423 14.9094 17.0984L18.6746 18.9728C19.3171 19.279 19.8618 19.7689 20.2446 20.3849C20.6274 21.0008 20.8322 21.7172 20.8351 22.4497V24.3863ZM3.47922 22.9129H19.3559V22.4562C19.3545 22.0021 19.2281 21.5578 18.9915 21.1755C18.7549 20.7932 18.4179 20.4887 18.0201 20.2977L14.246 18.4214C13.8896 18.248 13.5876 17.9739 13.3751 17.6306C13.1626 17.2873 13.0481 16.8888 13.0447 16.481V14.7394L13.224 14.5323C13.3136 14.4256 13.4212 14.29 13.5377 14.1294C13.9985 13.472 14.3612 12.7465 14.6136 11.9774L14.73 11.6116L15.0976 11.4984C15.1863 11.4735 15.2648 11.4195 15.3214 11.3445C15.378 11.2695 15.4095 11.1776 15.4113 11.0824V9.51252C15.4116 9.44991 15.398 9.38807 15.3716 9.33175C15.3452 9.27543 15.3067 9.22611 15.259 9.18758L15.0169 8.96848L15.0259 6.27614C15.0639 5.65 14.8608 5.03377 14.4611 4.56233C13.5677 3.91904 12.5057 3.57424 11.4176 3.57424C10.3294 3.57424 9.26736 3.91904 8.37398 4.56233C8.1759 4.79646 8.02477 5.06904 7.92935 5.36421C7.83393 5.65939 7.79613 5.97127 7.81816 6.28171V8.97405L7.5761 9.19408C7.52994 9.23359 7.49251 9.28285 7.46622 9.33857C7.43994 9.39429 7.42549 9.45518 7.42376 9.51716V11.088C7.42508 11.1629 7.44462 11.2362 7.48067 11.3012C7.51672 11.3661 7.5681 11.4205 7.62994 11.4594L7.88098 11.6218L7.95266 11.9143C8.19406 12.8749 8.62781 13.7723 9.2257 14.5481L9.39598 14.7524V16.4532C9.39507 16.8486 9.28819 17.236 9.08716 17.5722C8.88613 17.9084 8.59885 18.1802 8.25743 18.3573L4.74329 20.2679C4.36546 20.4676 4.04781 20.7708 3.82492 21.1446C3.60203 21.5183 3.48243 21.9482 3.47922 22.3875V22.9222V22.9129Z"
            fill="white"
          />
          <path
            d="M3.47922 22.9129H19.3559V22.4562C19.3545 22.0021 19.2281 21.5578 18.9915 21.1755C18.7549 20.7932 18.4179 20.4887 18.0201 20.2977L14.246 18.4214C13.8896 18.248 13.5876 17.9739 13.3751 17.6306C13.1626 17.2873 13.0481 16.8888 13.0447 16.481V14.7394L13.224 14.5323C13.3136 14.4256 13.4212 14.29 13.5377 14.1294C13.9985 13.472 14.3612 12.7465 14.6136 11.9774L14.73 11.6116L15.0976 11.4984C15.1863 11.4735 15.2648 11.4195 15.3214 11.3445C15.378 11.2695 15.4095 11.1776 15.4113 11.0824V9.51252C15.4116 9.44991 15.398 9.38807 15.3716 9.33175C15.3452 9.27543 15.3067 9.22611 15.259 9.18758L15.0169 8.96848L15.0259 6.27614C15.0639 5.65 14.8608 5.03377 14.4611 4.56233C13.5677 3.91904 12.5057 3.57424 11.4176 3.57424C10.3294 3.57424 9.26736 3.91904 8.37398 4.56233C8.1759 4.79646 8.02477 5.06904 7.92935 5.36421C7.83393 5.65939 7.79613 5.97127 7.81816 6.28171V8.97405L7.5761 9.19408C7.52994 9.23359 7.49251 9.28285 7.46622 9.33857C7.43994 9.39429 7.42549 9.45518 7.42376 9.51716V11.088C7.42508 11.1629 7.44462 11.2362 7.48067 11.3012C7.51672 11.3661 7.5681 11.4205 7.62994 11.4594L7.88098 11.6218L7.95266 11.9143C8.19406 12.8749 8.62781 13.7723 9.2257 14.5481L9.39598 14.7524V16.4532C9.39507 16.8486 9.28819 17.236 9.08716 17.5722C8.88613 17.9084 8.59885 18.1802 8.25743 18.3573L4.74329 20.2679C4.36546 20.4676 4.04781 20.7708 3.82492 21.1446C3.60203 21.5183 3.48243 21.9482 3.47922 22.3875V22.9129Z"
            fill="white"
          />
        </svg>
      </div>
      <div className={styles.label}>
        <div className={styles.email}>{account?.email}</div>
        <div className={styles.role}>{account?.role}</div>
      </div>
      <svg
        width="14"
        height="17"
        viewBox="0 0 14 17"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cl(styles.arrowIcon, expanded && styles.expanded)}
      >
        <path
          d="M2.91634 11.1091L6.99967 5.89154L11.083 11.1091"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div
        className={cl(styles.listContainer, expanded && styles.expanded)}
        onClick={(e) => {
          e.stopPropagation();
        }}
        ref={listRef}
      >
        <div className={styles.list}>
          <div className={styles.userInfo}>
            <div className={styles.userInfoIcon}>
              <svg
                width="13"
                height="15"
                viewBox="0 0 13 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_4544_19973)">
                  <rect
                    x="0.175293"
                    y="0.0244141"
                    width="12.65"
                    height="14.95"
                    rx="6.325"
                    fill="#1F93FF"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.1554 14.0467H1.3252V12.8926C1.32829 12.486 1.43904 12.0881 1.64536 11.7422C1.85167 11.3963 2.14567 11.1156 2.49534 10.9308L4.52113 9.83109C4.58331 9.79791 4.63552 9.74762 4.6719 9.68574C4.70827 9.62387 4.72743 9.5528 4.72733 9.48036V8.7992C4.37945 8.32151 4.11753 7.78281 3.95412 7.20893C3.84157 7.10804 3.75119 6.9834 3.68895 6.84337C3.62672 6.70333 3.5941 6.55113 3.59331 6.39698V5.49428C3.59492 5.25286 3.67476 5.01899 3.82009 4.8302V3.70917C3.79906 3.41768 3.83479 3.12479 3.92511 2.84786C4.01543 2.57093 4.1585 2.3156 4.34589 2.09701C5.02785 1.53711 5.87259 1.23242 6.74287 1.23242C7.61315 1.23242 8.45789 1.53711 9.13985 2.09701C9.32692 2.31563 9.46941 2.5711 9.55887 2.84811C9.64833 3.12513 9.68292 3.418 9.66049 3.70917V4.8302C9.80563 5.01868 9.88547 5.25215 9.88727 5.49322V6.39645C9.88576 6.59424 9.83214 6.78786 9.73222 6.95643C9.6323 7.125 9.48988 7.26214 9.32026 7.35306C9.15774 7.81032 8.93387 8.24161 8.65527 8.63425C8.60888 8.69617 8.56764 8.75276 8.5264 8.804V9.49798C8.52729 9.57318 8.54857 9.64661 8.58775 9.70993C8.62694 9.77325 8.68248 9.82392 8.74808 9.85618L10.9131 10.934C11.2825 11.11 11.5958 11.3917 11.8158 11.7459C12.0359 12.1001 12.1537 12.512 12.1554 12.9331V14.0467ZM2.17575 13.1995H11.3048V12.9369C11.304 12.6758 11.2313 12.4203 11.0953 12.2005C10.9593 11.9807 10.7655 11.8056 10.5368 11.6957L8.36664 10.6169C8.16169 10.5172 7.98809 10.3596 7.86588 10.1622C7.74367 9.96479 7.67785 9.73565 7.67591 9.50118V8.49972L7.77899 8.38068C7.83053 8.31929 7.89238 8.24135 7.95939 8.149C8.22432 7.77098 8.43288 7.35384 8.578 6.91159L8.64495 6.70126L8.85632 6.63614C8.90731 6.62183 8.95247 6.5908 8.985 6.54768C9.01752 6.50456 9.03566 6.45169 9.03672 6.39698V5.49428C9.03685 5.45828 9.02907 5.42273 9.01388 5.39034C8.99869 5.35795 8.97653 5.3296 8.94913 5.30744L8.80994 5.18146L8.8151 3.63337C8.83695 3.27334 8.72018 2.919 8.49035 2.64792C7.97665 2.27803 7.36596 2.07977 6.74029 2.07977C6.11462 2.07977 5.50393 2.27803 4.99023 2.64792C4.87634 2.78255 4.78944 2.93928 4.73457 3.10901C4.6797 3.27873 4.65797 3.45807 4.67064 3.63657V5.18466L4.53145 5.31118C4.50491 5.3339 4.48339 5.36222 4.46827 5.39426C4.45316 5.4263 4.44485 5.46131 4.44386 5.49695V6.40019C4.44462 6.44325 4.45585 6.48541 4.47658 6.52275C4.49731 6.56008 4.52685 6.59138 4.56241 6.61372L4.70676 6.70714L4.74797 6.87529C4.88678 7.42766 5.13619 7.94364 5.47997 8.38976L5.57789 8.5072V9.48517C5.57736 9.71254 5.5159 9.9353 5.40031 10.1286C5.28472 10.3219 5.11953 10.4782 4.92322 10.58L2.90259 11.6787C2.68534 11.7935 2.50269 11.9678 2.37452 12.1827C2.24636 12.3976 2.17759 12.6448 2.17575 12.8974V13.2049V13.1995Z"
                    fill="white"
                  />
                  <path
                    d="M2.17575 13.1995H11.3048V12.9369C11.304 12.6758 11.2313 12.4203 11.0953 12.2005C10.9593 11.9807 10.7655 11.8056 10.5368 11.6957L8.36664 10.6169C8.16169 10.5172 7.98809 10.3596 7.86588 10.1622C7.74367 9.96479 7.67785 9.73565 7.67591 9.50118V8.49972L7.77899 8.38068C7.83053 8.31929 7.89238 8.24135 7.95939 8.149C8.22432 7.77098 8.43288 7.35384 8.578 6.91159L8.64495 6.70126L8.85632 6.63614C8.90731 6.62183 8.95247 6.5908 8.985 6.54768C9.01752 6.50456 9.03566 6.45169 9.03672 6.39698V5.49428C9.03685 5.45828 9.02907 5.42273 9.01388 5.39034C8.99869 5.35795 8.97653 5.3296 8.94913 5.30744L8.80994 5.18146L8.8151 3.63337C8.83695 3.27334 8.72018 2.919 8.49035 2.64792C7.97665 2.27803 7.36596 2.07977 6.74029 2.07977C6.11462 2.07977 5.50393 2.27803 4.99023 2.64792C4.87634 2.78255 4.78944 2.93928 4.73457 3.10901C4.6797 3.27873 4.65797 3.45807 4.67064 3.63657V5.18466L4.53145 5.31118C4.50491 5.3339 4.48339 5.36222 4.46827 5.39426C4.45316 5.4263 4.44485 5.46131 4.44386 5.49695V6.40019C4.44462 6.44325 4.45585 6.48541 4.47658 6.52275C4.49731 6.56008 4.52685 6.59138 4.56241 6.61372L4.70676 6.70714L4.74797 6.87529C4.88678 7.42766 5.13619 7.94364 5.47997 8.38976L5.57789 8.5072V9.48517C5.57736 9.71254 5.5159 9.9353 5.40031 10.1286C5.28472 10.3219 5.11953 10.4782 4.92322 10.58L2.90259 11.6787C2.68534 11.7935 2.50269 11.9678 2.37452 12.1827C2.24636 12.3976 2.17759 12.6448 2.17575 12.8974V13.1995Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_4544_19973">
                    <rect
                      x="0.175293"
                      y="0.0244141"
                      width="12.65"
                      height="14.95"
                      rx="6.325"
                      fill="white"
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className={styles.userInfoLabel}>
              <div className={styles.userInfoEmail}>{account?.email}</div>
              <div className={styles.userInfoRole}>{account?.role}</div>
            </div>
          </div>
          {homePage && (
            <div
              className={cl(styles.item, styles.toHome)}
              onClick={() => {
                setExpanded(false);
                navigate(homePage);
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.5 7.49996L10 1.66663L17.5 7.49996V16.6666C17.5 17.1087 17.3244 17.5326 17.0118 17.8451C16.6993 18.1577 16.2754 18.3333 15.8333 18.3333H4.16667C3.72464 18.3333 3.30072 18.1577 2.98816 17.8451C2.67559 17.5326 2.5 17.1087 2.5 16.6666V7.49996Z"
                  stroke="#5B6C7B"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.5 18.3333V10H12.5V18.3333"
                  stroke="#5B6C7B"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{t('general.toHomePage')}</span>
            </div>
          )}
          <div
            className={styles.item}
            onClick={() => {
              setExpanded(false);
              dispatch(logoutAction());
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.9993 6.66667L18.3327 10M18.3327 10L14.9993 13.3333M18.3327 10H7.49935M12.4993 3.50337C11.4371 2.86523 10.2037 2.5 8.88824 2.5C4.89951 2.5 1.66602 5.85786 1.66602 10C1.66602 14.1421 4.89951 17.5 8.88824 17.5C10.2037 17.5 11.4371 17.1348 12.4993 16.4966"
                stroke="#5B6C7B"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{t('general.logout')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
